import { EventEmitter } from 'events'

import { AbortError } from '@app/errors/AbortError'

import { loadScript } from './loadScript'

export default {
  get(options: { type: 'youtube' | 'vimeo'; id: string }) {
    const { type, id } = options
    if (type === 'youtube') return new YoutubePlayerApi(id)
    return new VimeoPlayerApi(id)
  },
}

class YoutubePlayerApi extends EventEmitter {
  readonly domId: string
  readonly src: string
  private player: any

  readonly name = 'youtube'

  constructor(videoId: string) {
    super()
    this.domId = `video-youtube-${videoId}`
    this.src = `https://www.youtube.com/embed/${videoId}?enablejsapi=1&amp;rel=0&amp;controls=0&amp;showinfo=0`
  }

  async load(abortSignal?: AbortSignal) {
    await loadScript(YOUTUBE_API).then(() => {
      return new Promise<any>(resolve => {
        const yt = (window as any).YT
        if (!yt?.ready) throw new YoutubeLoadError('Youtube API not loaded')
        yt.ready(() => {
          resolve(yt)
        })
      }).then(yt => {
        if (abortSignal?.aborted) throw new AbortError()
        new yt.Player(this.domId).addEventListener('onReady', this.handleReady.bind(this))
      })
    })
  }

  unload() {
    CHANNEL.removeListener('play', this.handlePlay)
    if (this.player) {
      this.player.removeEventListener('onReady')
      this.player.removeEventListener('onStateChange')
    }
  }

  seekTo(time: number) {
    if (this.player) {
      this.player.seekTo(time)
    }
  }

  pause() {
    this.player?.pauseVideo()
  }

  getState() {
    if (this.player) return null
    const [duration, seconds] = [this.player.getDuration(), this.player.getCurrentTime()]
    return {
      duration,
      seconds,
      percent: seconds / duration,
    }
  }

  private handleReady(event) {
    this.handlePlay = this.handlePlay.bind(this)
    CHANNEL.addListener('play', this.handlePlay)
    this.player = event.target
    this.player.addEventListener('onStateChange', this.handleStateChange.bind(this))
    this.emit('ready', this)
  }

  private handlePlay(api) {
    if (api !== this) {
      this.pause()
    }
  }

  private handleStateChange(event) {
    if (event.data === (window as any).YT.PlayerState.PLAYING) {
      this.emit('play', this)
      CHANNEL.emit('play', this)
    } else if (event.data === (window as any).YT.PlayerState.PAUSED) {
      this.emit('pause', this)
    } else if (event.data === (window as any).YT.PlayerState.ENDED) {
      this.emit('finish', this)
    }
  }
}

class VimeoPlayerApi extends EventEmitter {
  readonly domId: string
  readonly src: string
  private player: any
  private state: any

  readonly name = 'vimeo'

  constructor(videoId: string) {
    super()
    this.domId = `video-vimeo-${videoId}`
    this.src = `//player.vimeo.com/video/${videoId}?api=1&amp;player_id=${this.domId}`
  }

  load(abortSignal?: AbortSignal) {
    return loadScript(VIMEO_API).then(() => {
      if (abortSignal?.aborted) throw new AbortError()
      const vapi = (window as any).$f
      if (!vapi) throw new VimeoLoadError('Vimeo API not loaded')
      this.player = vapi(this.domId)
      this.player.addEvent('ready', this.handleReady.bind(this))
    })
  }

  unload() {
    CHANNEL.removeListener('play', this.handlePlay)
    if (this.player) {
      this.player.removeEvent('ready')
      this.player.removeEvent('play')
      this.player.removeEvent('pause')
      this.player.removeEvent('finish')
    }
    return this
  }

  seekTo(time: string) {
    this.player?.api('seekTo', time)
  }

  pause() {
    this.player?.api('pause')
  }

  getState() {
    if (this.state) return null
    return this.state
  }

  private handleReady() {
    this.handlePlay = this.handlePlay.bind(this)
    CHANNEL.addListener('play', this.handlePlay)
    this.player.addEvent('play', this.handleEvent('play').bind(this))
    this.player.addEvent('playProgress', this.handlePlayProgress.bind(this))
    this.player.addEvent('seek', this.handleSeek.bind(this))
    this.player.addEvent('pause', this.handleEvent('pause').bind(this))
    this.player.addEvent('finish', this.handleEvent('finish').bind(this))
    this.emit('ready', this)
  }

  private handlePlay(api) {
    if (api !== this) {
      this.pause()
    }
  }

  private handlePlayProgress(data) {
    this.state = data
  }

  private handleSeek(data) {
    this.state = data
    this.emit('seek', this)
  }

  private handleEvent(type) {
    return () => {
      this.emit(type, this)

      if (type === 'play') CHANNEL.emit('play', this)
    }
  }
}

export class YoutubeLoadError extends Error {}
export class VimeoLoadError extends Error {}

// Youtube has bug when loads API by http and authorized user redirected to https
// Iframe should be loaded by https as well
const YOUTUBE_API = 'https://www.youtube.com/iframe_api'

const VIMEO_API = '//f.vimeocdn.com/js/froogaloop2.min.js'

const CHANNEL = new EventEmitter()
